/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Button,
    TextField,
    Grid,
    Paper,
    Chip,
    Rating,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Slider,
    Divider,
    IconButton,
    Collapse,
    CircularProgress,
    Alert,
    Skeleton,
    useMediaQuery,
    useTheme,
    Drawer,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Breadcrumbs,
    Link,
    Badge,
} from '@mui/material';
import {
    Hotel,
    LocationOn,
    CalendarToday,
    Person,
    Search,
    FilterList,
    Sort,
    Map,
    ViewList,
    ExpandMore,
    ExpandLess,
    Pool,
    Wifi,
    FreeBreakfast,
    LocalParking,
    FitnessCenter,
    Spa,
    AcUnit,
    Restaurant,
    Pets,
    SmokeFree,
    Accessible,
    BusinessCenter,
    BeachAccess,
    Landscape,
    LocationCity,
    Flight,
    Star,
    CheckCircle,
    Close,
    TuneRounded,
    ArrowBack,
    Home,
    MeetingRoom,
    Add,
    Remove,
} from '@mui/icons-material';

// ============================================================================
// TYPES
// ============================================================================

interface HotelResult {
    id: string;
    name: string;
    location: string;
    neighborhood: string;
    address: string;
    rating: number;
    reviewCount: number;
    reviewScore: number;
    pricePerNight: number;
    originalPrice?: number;
    totalPrice: number;
    currency: string;
    image: string;
    images: string[];
    amenities: string[];
    freeCancellation: boolean;
    payLater: boolean;
    recommended?: boolean;
    starRating: number;
    distanceFromCenter: number;
    distanceFromAirport?: number;
    propertyType: 'hotel' | 'resort' | 'apartment' | 'villa' | 'hostel' | 'bnb';
    highlights: string[];
}

interface SearchFilters {
    priceRange: [number, number];
    starRatings: number[];
    reviewScore: number;
    propertyTypes: string[];
    amenities: string[];
    neighborhoods: string[];
    freeCancellation: boolean;
    payLater: boolean;
    distanceFromCenter: number;
}

type SortOption = 'recommended' | 'price_low' | 'price_high' | 'rating' | 'distance' | 'reviews';

// ============================================================================
// CONSTANTS
// ============================================================================

const AMENITY_OPTIONS = [
    { key: 'wifi', label: 'Free WiFi', icon: <Wifi /> },
    { key: 'pool', label: 'Pool', icon: <Pool /> },
    { key: 'breakfast', label: 'Breakfast Included', icon: <FreeBreakfast /> },
    { key: 'parking', label: 'Free Parking', icon: <LocalParking /> },
    { key: 'gym', label: 'Fitness Center', icon: <FitnessCenter /> },
    { key: 'spa', label: 'Spa', icon: <Spa /> },
    { key: 'ac', label: 'Air Conditioning', icon: <AcUnit /> },
    { key: 'restaurant', label: 'Restaurant', icon: <Restaurant /> },
    { key: 'petFriendly', label: 'Pet Friendly', icon: <Pets /> },
    { key: 'nonSmoking', label: 'Non-Smoking', icon: <SmokeFree /> },
    { key: 'accessible', label: 'Wheelchair Accessible', icon: <Accessible /> },
    { key: 'businessCenter', label: 'Business Center', icon: <BusinessCenter /> },
];

const PROPERTY_TYPES = [
    { key: 'hotel', label: 'Hotels' },
    { key: 'resort', label: 'Resorts' },
    { key: 'apartment', label: 'Apartments' },
    { key: 'villa', label: 'Villas' },
    { key: 'hostel', label: 'Hostels' },
    { key: 'bnb', label: 'B&Bs' },
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'recommended', label: 'Recommended' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Guest Rating' },
    { value: 'distance', label: 'Distance from Center' },
    { value: 'reviews', label: 'Most Reviews' },
];

// ============================================================================
// MOCK DATA GENERATOR
// ============================================================================

const generateNeighborhoods = (destination: string): string[] => {
    const genericNeighborhoods = [
        'Downtown',
        'Business District',
        'Historic Center',
        'Waterfront',
        'Arts District',
        'University Area',
        'Airport Area',
        'Shopping District',
        'Beach Area',
        'Suburban',
    ];
    return genericNeighborhoods;
};

const generateMockHotels = (
    destination: string,
    checkIn: string,
    checkOut: string,
    rooms: number,
    guests: number
): HotelResult[] => {
    const nights = checkIn && checkOut
        ? Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24))
        : 1;

    const neighborhoods = generateNeighborhoods(destination);

    const hotels: HotelResult[] = [
        {
            id: 'hotel-1',
            name: `${destination} Grand Hotel & Spa`,
            location: `Downtown ${destination}`,
            neighborhood: 'Downtown',
            address: `123 Main Street, ${destination}`,
            rating: 4.5,
            reviewCount: 2847,
            reviewScore: 8.9,
            pricePerNight: 189,
            originalPrice: 229,
            totalPrice: 189 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600',
            images: [
                'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600',
                'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600',
            ],
            amenities: ['pool', 'wifi', 'breakfast', 'parking', 'gym', 'spa', 'ac', 'restaurant'],
            freeCancellation: true,
            payLater: true,
            recommended: true,
            starRating: 5,
            distanceFromCenter: 0.3,
            distanceFromAirport: 12.5,
            propertyType: 'hotel',
            highlights: ['Rooftop pool', 'Award-winning spa', 'City views'],
        },
        {
            id: 'hotel-2',
            name: `Hilton ${destination}`,
            location: `Business District, ${destination}`,
            neighborhood: 'Business District',
            address: `456 Commerce Ave, ${destination}`,
            rating: 4.3,
            reviewCount: 1923,
            reviewScore: 8.5,
            pricePerNight: 159,
            totalPrice: 159 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600',
            images: ['https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600'],
            amenities: ['wifi', 'breakfast', 'parking', 'gym', 'ac', 'businessCenter'],
            freeCancellation: true,
            payLater: false,
            starRating: 4,
            distanceFromCenter: 1.2,
            distanceFromAirport: 10.0,
            propertyType: 'hotel',
            highlights: ['Executive lounge', 'Meeting rooms'],
        },
        {
            id: 'hotel-3',
            name: `${destination} Beach Resort`,
            location: `Beachfront, ${destination}`,
            neighborhood: 'Beach Area',
            address: `789 Ocean Drive, ${destination}`,
            rating: 4.7,
            reviewCount: 3156,
            reviewScore: 9.2,
            pricePerNight: 279,
            originalPrice: 329,
            totalPrice: 279 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600',
            images: ['https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600'],
            amenities: ['pool', 'wifi', 'breakfast', 'parking', 'gym', 'spa', 'ac', 'restaurant', 'petFriendly'],
            freeCancellation: true,
            payLater: true,
            starRating: 5,
            distanceFromCenter: 3.5,
            distanceFromAirport: 15.0,
            propertyType: 'resort',
            highlights: ['Private beach', 'Ocean views', 'Water sports'],
        },
        {
            id: 'hotel-4',
            name: `Holiday Inn Express ${destination}`,
            location: `Near Airport, ${destination}`,
            neighborhood: 'Airport Area',
            address: `101 Airport Blvd, ${destination}`,
            rating: 4.0,
            reviewCount: 892,
            reviewScore: 7.8,
            pricePerNight: 99,
            totalPrice: 99 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600',
            images: ['https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600'],
            amenities: ['wifi', 'breakfast', 'parking', 'ac'],
            freeCancellation: true,
            payLater: false,
            starRating: 3,
            distanceFromCenter: 8.0,
            distanceFromAirport: 1.5,
            propertyType: 'hotel',
            highlights: ['Free airport shuttle', '24-hour front desk'],
        },
        {
            id: 'hotel-5',
            name: `Boutique Hotel ${destination}`,
            location: `Historic District, ${destination}`,
            neighborhood: 'Historic Center',
            address: `222 Heritage Lane, ${destination}`,
            rating: 4.8,
            reviewCount: 456,
            reviewScore: 9.4,
            pricePerNight: 199,
            totalPrice: 199 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600',
            images: ['https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600'],
            amenities: ['wifi', 'breakfast', 'spa', 'ac', 'restaurant'],
            freeCancellation: false,
            payLater: true,
            starRating: 4,
            distanceFromCenter: 0.5,
            propertyType: 'bnb',
            highlights: ['Charming decor', 'Local breakfast', 'Walking tours'],
        },
        {
            id: 'hotel-6',
            name: `${destination} Apartments`,
            location: `Arts District, ${destination}`,
            neighborhood: 'Arts District',
            address: `333 Gallery Street, ${destination}`,
            rating: 4.4,
            reviewCount: 234,
            reviewScore: 8.7,
            pricePerNight: 129,
            totalPrice: 129 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600',
            images: ['https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600'],
            amenities: ['wifi', 'parking', 'ac', 'petFriendly'],
            freeCancellation: true,
            payLater: true,
            starRating: 4,
            distanceFromCenter: 1.8,
            propertyType: 'apartment',
            highlights: ['Full kitchen', 'Washer/dryer', 'Local neighborhood feel'],
        },
        {
            id: 'hotel-7',
            name: `Luxury Waterfront Villa`,
            location: `Waterfront, ${destination}`,
            neighborhood: 'Waterfront',
            address: `500 Marina Way, ${destination}`,
            rating: 4.9,
            reviewCount: 89,
            reviewScore: 9.6,
            pricePerNight: 449,
            originalPrice: 549,
            totalPrice: 449 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600',
            images: ['https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600'],
            amenities: ['pool', 'wifi', 'parking', 'spa', 'ac', 'petFriendly'],
            freeCancellation: false,
            payLater: false,
            starRating: 5,
            distanceFromCenter: 2.5,
            propertyType: 'villa',
            highlights: ['Private pool', 'Butler service', 'Yacht access'],
        },
        {
            id: 'hotel-8',
            name: `${destination} Hostel`,
            location: `University Area, ${destination}`,
            neighborhood: 'University Area',
            address: `42 Campus Road, ${destination}`,
            rating: 4.1,
            reviewCount: 567,
            reviewScore: 7.5,
            pricePerNight: 45,
            totalPrice: 45 * nights * rooms,
            currency: 'USD',
            image: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=600',
            images: ['https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=600'],
            amenities: ['wifi', 'ac'],
            freeCancellation: true,
            payLater: false,
            starRating: 2,
            distanceFromCenter: 2.0,
            propertyType: 'hostel',
            highlights: ['Social atmosphere', 'Common kitchen', 'Events'],
        },
    ];

    return hotels;
};

// ============================================================================
// STEPPER INPUT COMPONENT
// ============================================================================

interface StepperInputProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    label: string;
    icon?: React.ReactNode;
}

const StepperInput: React.FC<StepperInputProps> = ({
    value,
    onChange,
    min = 1,
    max = 9,
    label,
    icon,
}) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: 2,
        px: 1.5,
        py: 1,
        bgcolor: 'white',
        minHeight: 56,
        '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {icon}
            <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
                size="small"
                onClick={() => value > min && onChange(value - 1)}
                disabled={value <= min}
                sx={{ bgcolor: '#f5f5f5', width: 32, height: 32 }}
            >
                <Remove fontSize="small" />
            </IconButton>
            <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>{value}</Typography>
            <IconButton
                size="small"
                onClick={() => value < max && onChange(value + 1)}
                disabled={value >= max}
                sx={{ bgcolor: '#f5f5f5', width: 32, height: 32 }}
            >
                <Add fontSize="small" />
            </IconButton>
        </Box>
    </Box>
);

// ============================================================================
// HOTEL CARD COMPONENT
// ============================================================================

interface HotelCardProps {
    hotel: HotelResult;
    nights: number;
    onSelect: (hotel: HotelResult) => void;
}

const HotelCard: React.FC<HotelCardProps> = ({ hotel, nights, onSelect }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const getScoreLabel = (score: number) => {
        if (score >= 9) return 'Exceptional';
        if (score >= 8) return 'Excellent';
        if (score >= 7) return 'Very Good';
        if (score >= 6) return 'Good';
        return 'Pleasant';
    };

    const amenityIcons: Record<string, React.ReactElement> = {
        pool: <Pool sx={{ fontSize: 16 }} />,
        wifi: <Wifi sx={{ fontSize: 16 }} />,
        breakfast: <FreeBreakfast sx={{ fontSize: 16 }} />,
        parking: <LocalParking sx={{ fontSize: 16 }} />,
        gym: <FitnessCenter sx={{ fontSize: 16 }} />,
        spa: <Spa sx={{ fontSize: 16 }} />,
        ac: <AcUnit sx={{ fontSize: 16 }} />,
        restaurant: <Restaurant sx={{ fontSize: 16 }} />,
        petFriendly: <Pets sx={{ fontSize: 16 }} />,
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderColor: '#2D6E6A',
                },
            }}
        >
            {/* Image */}
            <Box sx={{ position: 'relative', width: { xs: '100%', sm: 240 }, minWidth: { sm: 240 } }}>
                <CardMedia
                    component="img"
                    sx={{
                        height: { xs: 200, sm: '100%' },
                        minHeight: { sm: 220 },
                        objectFit: 'cover',
                    }}
                    image={hotel.image}
                    alt={hotel.name}
                />
                {hotel.recommended && (
                    <Chip
                        label="Top Pick"
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            bgcolor: '#2D6E6A',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                        }}
                    />
                )}
                {hotel.originalPrice && (
                    <Chip
                        label={`${Math.round((1 - hotel.pricePerNight / hotel.originalPrice) * 100)}% OFF`}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: '#FF5722',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                        }}
                    />
                )}
            </Box>

            {/* Content */}
            <CardContent sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, pr: 2 }}>
                        {/* Star rating */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Rating value={hotel.starRating} size="small" readOnly />
                            <Chip
                                label={hotel.propertyType}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                            />
                        </Box>

                        {/* Hotel name */}
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.3, mb: 0.5 }}>
                            {hotel.name}
                        </Typography>

                        {/* Location */}
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <LocationOn sx={{ fontSize: 16 }} />
                            {hotel.location} · {hotel.distanceFromCenter} km from center
                        </Typography>

                        {/* Highlights */}
                        {hotel.highlights && hotel.highlights.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                {hotel.highlights.map((highlight) => (
                                    <Chip
                                        key={highlight}
                                        label={highlight}
                                        size="small"
                                        sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#E8F5E9', color: '#2E7D32' }}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Amenities */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {hotel.amenities.slice(0, 5).map((amenity) => (
                                <Chip
                                    key={amenity}
                                    icon={amenityIcons[amenity]}
                                    label={amenity.replace(/([A-Z])/g, ' $1').trim()}
                                    size="small"
                                    sx={{ height: 24, fontSize: '0.7rem', textTransform: 'capitalize' }}
                                />
                            ))}
                            {hotel.amenities.length > 5 && (
                                <Chip
                                    label={`+${hotel.amenities.length - 5} more`}
                                    size="small"
                                    sx={{ height: 24, fontSize: '0.7rem' }}
                                />
                            )}
                        </Box>

                        {/* Cancellation & Pay later */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {hotel.freeCancellation && (
                                <Typography variant="caption" sx={{ color: '#2E7D32', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckCircle sx={{ fontSize: 14 }} /> Free cancellation
                                </Typography>
                            )}
                            {hotel.payLater && (
                                <Typography variant="caption" sx={{ color: '#1976D2' }}>
                                    Reserve now, pay later
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Rating & Price */}
                    <Box sx={{ textAlign: 'right', minWidth: { xs: 80, sm: 120 } }}>
                        {/* Review score */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {getScoreLabel(hotel.reviewScore)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {hotel.reviewCount.toLocaleString()} reviews
                                </Typography>
                            </Box>
                            <Chip
                                label={hotel.reviewScore.toFixed(1)}
                                sx={{
                                    bgcolor: hotel.reviewScore >= 8 ? '#2D6E6A' : hotel.reviewScore >= 7 ? '#FFC107' : '#999',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    height: 32,
                                }}
                            />
                        </Box>

                        {/* Price */}
                        <Box sx={{ mt: 2 }}>
                            {hotel.originalPrice && (
                                <Typography
                                    variant="body2"
                                    sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                >
                                    ${hotel.originalPrice}
                                </Typography>
                            )}
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                ${hotel.pricePerNight}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                per night
                            </Typography>
                            {nights > 1 && (
                                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    ${hotel.totalPrice} total
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block">
                                includes taxes & fees
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Book button */}
                <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={() => onSelect(hotel)}
                        sx={{
                            bgcolor: '#2D6E6A',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        See Availability
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

// ============================================================================
// FILTER SIDEBAR COMPONENT
// ============================================================================

interface FilterSidebarProps {
    filters: SearchFilters;
    onChange: (filters: SearchFilters) => void;
    neighborhoods: string[];
    maxPrice: number;
    resultCount: number;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
    filters,
    onChange,
    neighborhoods,
    maxPrice,
    resultCount,
}) => {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        price: true,
        stars: true,
        rating: true,
        propertyType: true,
        location: true,
        amenities: false,
        policies: true,
    });

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const updateFilter = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
        onChange({ ...filters, [key]: value });
    };

    const toggleArrayFilter = (key: 'starRatings' | 'propertyTypes' | 'amenities' | 'neighborhoods', value: string | number) => {
        const current = filters[key] as (string | number)[];
        const updated = current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
        updateFilter(key, updated as any);
    };

    const FilterSection: React.FC<{ title: string; id: string; children: React.ReactNode }> = ({ title, id, children }) => (
        <Box sx={{ mb: 2 }}>
            <Box
                onClick={() => toggleSection(id)}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    py: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                {expandedSections[id] ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={expandedSections[id]}>
                <Box sx={{ pt: 1 }}>{children}</Box>
            </Collapse>
            <Divider sx={{ mt: 2 }} />
        </Box>
    );

    return (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList /> Filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {resultCount} properties found
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Price Range */}
            <FilterSection title="Price per night" id="price">
                <Slider
                    value={filters.priceRange}
                    onChange={(_, value) => updateFilter('priceRange', value as [number, number])}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `$${v}`}
                    min={0}
                    max={maxPrice}
                    sx={{ color: '#2D6E6A' }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">${filters.priceRange[0]}</Typography>
                    <Typography variant="caption">${filters.priceRange[1]}</Typography>
                </Box>
            </FilterSection>

            {/* Star Rating */}
            <FilterSection title="Star Rating" id="stars">
                <FormGroup>
                    {[5, 4, 3, 2, 1].map((stars) => (
                        <FormControlLabel
                            key={stars}
                            control={
                                <Checkbox
                                    checked={filters.starRatings.includes(stars)}
                                    onChange={() => toggleArrayFilter('starRatings', stars)}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Rating value={stars} size="small" readOnly />
                                </Box>
                            }
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Review Score */}
            <FilterSection title="Guest Rating" id="rating">
                <FormGroup>
                    {[9, 8, 7, 6].map((score) => (
                        <FormControlLabel
                            key={score}
                            control={
                                <Checkbox
                                    checked={filters.reviewScore <= score && filters.reviewScore > score - 1}
                                    onChange={() => updateFilter('reviewScore', score)}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                    size="small"
                                />
                            }
                            label={`${score}+ ${score >= 9 ? 'Exceptional' : score >= 8 ? 'Excellent' : score >= 7 ? 'Very Good' : 'Good'}`}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Property Type */}
            <FilterSection title="Property Type" id="propertyType">
                <FormGroup>
                    {PROPERTY_TYPES.map((type) => (
                        <FormControlLabel
                            key={type.key}
                            control={
                                <Checkbox
                                    checked={filters.propertyTypes.includes(type.key)}
                                    onChange={() => toggleArrayFilter('propertyTypes', type.key)}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                    size="small"
                                />
                            }
                            label={type.label}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Location / Neighborhood */}
            <FilterSection title="Location" id="location">
                <FormGroup>
                    {neighborhoods.map((hood) => (
                        <FormControlLabel
                            key={hood}
                            control={
                                <Checkbox
                                    checked={filters.neighborhoods.includes(hood)}
                                    onChange={() => toggleArrayFilter('neighborhoods', hood)}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {hood === 'Beach Area' ? <BeachAccess sx={{ fontSize: 16 }} /> :
                                     hood === 'Business District' ? <BusinessCenter sx={{ fontSize: 16 }} /> :
                                     hood === 'Downtown' ? <LocationCity sx={{ fontSize: 16 }} /> :
                                     hood === 'Airport Area' ? <Flight sx={{ fontSize: 16 }} /> :
                                     <LocationOn sx={{ fontSize: 16 }} />}
                                    {hood}
                                </Box>
                            }
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Amenities */}
            <FilterSection title="Amenities" id="amenities">
                <FormGroup>
                    {AMENITY_OPTIONS.map((amenity) => (
                        <FormControlLabel
                            key={amenity.key}
                            control={
                                <Checkbox
                                    checked={filters.amenities.includes(amenity.key)}
                                    onChange={() => toggleArrayFilter('amenities', amenity.key)}
                                    sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {amenity.icon}
                                    {amenity.label}
                                </Box>
                            }
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Policies */}
            <FilterSection title="Booking Policies" id="policies">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.freeCancellation}
                            onChange={(e) => updateFilter('freeCancellation', e.target.checked)}
                            sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                            size="small"
                        />
                    }
                    label="Free cancellation"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.payLater}
                            onChange={(e) => updateFilter('payLater', e.target.checked)}
                            sx={{ '&.Mui-checked': { color: '#2D6E6A' } }}
                            size="small"
                        />
                    }
                    label="Reserve now, pay later"
                />
            </FilterSection>
        </Paper>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HotelSearchResultsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Parse URL params
    const initialDestination = searchParams.get('destination') || '';
    const initialCheckIn = searchParams.get('checkIn') || '';
    const initialCheckOut = searchParams.get('checkOut') || '';
    const initialRooms = parseInt(searchParams.get('rooms') || '1', 10);
    const initialAdults = parseInt(searchParams.get('adults') || '2', 10);
    const initialChildren = parseInt(searchParams.get('children') || '0', 10);

    // Search state
    const [destination, setDestination] = useState(initialDestination);
    const [checkIn, setCheckIn] = useState(initialCheckIn);
    const [checkOut, setCheckOut] = useState(initialCheckOut);
    const [rooms, setRooms] = useState(initialRooms);
    const [guests, setGuests] = useState(initialAdults + initialChildren);

    // Results state
    const [loading, setLoading] = useState(true);
    const [hotels, setHotels] = useState<HotelResult[]>([]);
    const [neighborhoods, setNeighborhoods] = useState<string[]>([]);

    // Filter state
    const [filters, setFilters] = useState<SearchFilters>({
        priceRange: [0, 500],
        starRatings: [],
        reviewScore: 0,
        propertyTypes: [],
        amenities: [],
        neighborhoods: [],
        freeCancellation: false,
        payLater: false,
        distanceFromCenter: 50,
    });

    // Sort state
    const [sortBy, setSortBy] = useState<SortOption>('recommended');

    // Mobile filter drawer
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Search form expanded state
    const [searchExpanded, setSearchExpanded] = useState(false);

    // Calculate nights
    const nights = checkIn && checkOut
        ? Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24))
        : 1;

    // Load results on mount
    useEffect(() => {
        const loadResults = async () => {
            setLoading(true);
            // Simulate API call
            await new Promise((resolve) => setTimeout(resolve, 1200));

            if (initialDestination) {
                const mockHotels = generateMockHotels(
                    initialDestination,
                    initialCheckIn,
                    initialCheckOut,
                    initialRooms,
                    initialAdults + initialChildren
                );
                setHotels(mockHotels);
                setNeighborhoods(generateNeighborhoods(initialDestination));

                // Set max price for filter
                const maxPrice = Math.max(...mockHotels.map((h) => h.pricePerNight), 500);
                setFilters((prev) => ({ ...prev, priceRange: [0, maxPrice] }));
            }

            setLoading(false);
        };

        loadResults();
    }, [initialDestination, initialCheckIn, initialCheckOut, initialRooms, initialAdults, initialChildren]);

    // Filter and sort hotels
    const filteredHotels = useMemo(() => {
        let result = [...hotels];

        // Apply filters
        result = result.filter((hotel) => {
            if (hotel.pricePerNight < filters.priceRange[0] || hotel.pricePerNight > filters.priceRange[1]) return false;
            if (filters.starRatings.length > 0 && !filters.starRatings.includes(hotel.starRating)) return false;
            if (filters.reviewScore > 0 && hotel.reviewScore < filters.reviewScore) return false;
            if (filters.propertyTypes.length > 0 && !filters.propertyTypes.includes(hotel.propertyType)) return false;
            if (filters.neighborhoods.length > 0 && !filters.neighborhoods.includes(hotel.neighborhood)) return false;
            if (filters.amenities.length > 0 && !filters.amenities.every((a) => hotel.amenities.includes(a))) return false;
            if (filters.freeCancellation && !hotel.freeCancellation) return false;
            if (filters.payLater && !hotel.payLater) return false;
            return true;
        });

        // Apply sort
        switch (sortBy) {
            case 'price_low':
                result.sort((a, b) => a.pricePerNight - b.pricePerNight);
                break;
            case 'price_high':
                result.sort((a, b) => b.pricePerNight - a.pricePerNight);
                break;
            case 'rating':
                result.sort((a, b) => b.reviewScore - a.reviewScore);
                break;
            case 'distance':
                result.sort((a, b) => a.distanceFromCenter - b.distanceFromCenter);
                break;
            case 'reviews':
                result.sort((a, b) => b.reviewCount - a.reviewCount);
                break;
            case 'recommended':
            default:
                result.sort((a, b) => {
                    if (a.recommended && !b.recommended) return -1;
                    if (!a.recommended && b.recommended) return 1;
                    return b.reviewScore - a.reviewScore;
                });
        }

        return result;
    }, [hotels, filters, sortBy]);

    // Handle search update
    const handleSearch = () => {
        const params = new URLSearchParams({
            destination,
            checkIn,
            checkOut,
            rooms: rooms.toString(),
            adults: guests.toString(),
            children: '0',
        });
        setSearchParams(params);
        setSearchExpanded(false);
    };

    // Handle hotel selection - navigate to checkout
    const handleSelectHotel = (hotel: HotelResult) => {
        // Navigate to guest checkout with hotel details
        const params = new URLSearchParams({
            type: 'hotel',
            hotelId: hotel.id,
            hotelName: hotel.name,
            destination,
            checkIn,
            checkOut,
            rooms: rooms.toString(),
            guests: guests.toString(),
            nights: nights.toString(),
            pricePerNight: hotel.pricePerNight.toString(),
            totalPrice: hotel.totalPrice.toString(),
        });
        navigate(`/book/checkout?${params.toString()}`);
    };

    // Calculate active filter count
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.starRatings.length > 0) count++;
        if (filters.reviewScore > 0) count++;
        if (filters.propertyTypes.length > 0) count++;
        if (filters.neighborhoods.length > 0) count++;
        if (filters.amenities.length > 0) count++;
        if (filters.freeCancellation) count++;
        if (filters.payLater) count++;
        return count;
    }, [filters]);

    const today = new Date().toISOString().split('T')[0];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F5F5F5' }}>
            {/* Header / Search Bar */}
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="xl">
                    {/* Breadcrumbs */}
                    <Box sx={{ py: 1 }}>
                        <Breadcrumbs sx={{ fontSize: '0.85rem' }}>
                            <Link
                                component="button"
                                onClick={() => navigate('/')}
                                underline="hover"
                                color="inherit"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                                <Home sx={{ fontSize: 16 }} /> Home
                            </Link>
                            <Link component="button" onClick={() => navigate(-1)} underline="hover" color="inherit">
                                Hotels
                            </Link>
                            <Typography color="text.primary" sx={{ fontSize: '0.85rem' }}>
                                {destination || 'Search Results'}
                            </Typography>
                        </Breadcrumbs>
                    </Box>

                    {/* Search Summary / Form */}
                    <Box sx={{ py: 2 }}>
                        {!searchExpanded ? (
                            <Paper
                                onClick={() => setSearchExpanded(true)}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                    '&:hover': { borderColor: '#2D6E6A' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LocationOn sx={{ color: '#2D6E6A' }} />
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {destination || 'Where?'}
                                        </Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CalendarToday sx={{ fontSize: 18, color: '#666' }} />
                                        <Typography variant="body2">
                                            {checkIn && checkOut
                                                ? `${new Date(checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                                : 'Select dates'}
                                        </Typography>
                                    </Box>
                                    <Divider orientation="vertical" flexItem />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Person sx={{ fontSize: 18, color: '#666' }} />
                                        <Typography variant="body2">
                                            {rooms} room{rooms > 1 ? 's' : ''}, {guests} guest{guests > 1 ? 's' : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Button
                                    variant="contained"
                                    sx={{
                                        bgcolor: '#2D6E6A',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#009688' },
                                    }}
                                >
                                    Modify Search
                                </Button>
                            </Paper>
                        ) : (
                            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #2D6E6A' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Modify your search
                                    </Typography>
                                    <IconButton size="small" onClick={() => setSearchExpanded(false)}>
                                        <Close />
                                    </IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Destination"
                                            value={destination}
                                            onChange={(e) => setDestination(e.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LocationOn color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Check-in"
                                            value={checkIn}
                                            onChange={(e) => setCheckIn(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: today }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Check-out"
                                            value={checkOut}
                                            onChange={(e) => setCheckOut(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: checkIn || today }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <StepperInput
                                            value={rooms}
                                            onChange={setRooms}
                                            min={1}
                                            max={9}
                                            label="Rooms"
                                            icon={<MeetingRoom sx={{ fontSize: 18, color: '#666' }} />}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <StepperInput
                                            value={guests}
                                            onChange={setGuests}
                                            min={1}
                                            max={20}
                                            label="Guests"
                                            icon={<Person sx={{ fontSize: 18, color: '#666' }} />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={1}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={handleSearch}
                                            sx={{
                                                height: 56,
                                                bgcolor: '#2D6E6A',
                                                borderRadius: 2,
                                                '&:hover': { bgcolor: '#009688' },
                                            }}
                                        >
                                            <Search />
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                    </Box>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Grid container spacing={3}>
                    {/* Filter Sidebar - Desktop */}
                    {!isMobile && (
                        <Grid item md={3}>
                            <FilterSidebar
                                filters={filters}
                                onChange={setFilters}
                                neighborhoods={neighborhoods}
                                maxPrice={Math.max(...hotels.map((h) => h.pricePerNight), 500)}
                                resultCount={filteredHotels.length}
                            />
                        </Grid>
                    )}

                    {/* Results */}
                    <Grid item xs={12} md={9}>
                        {/* Results Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {destination}: {filteredHotels.length} properties found
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* Mobile Filter Button */}
                                {isMobile && (
                                    <Button
                                        variant="outlined"
                                        startIcon={
                                            <Badge badgeContent={activeFilterCount} color="primary">
                                                <TuneRounded />
                                            </Badge>
                                        }
                                        onClick={() => setFilterDrawerOpen(true)}
                                        sx={{ borderRadius: 2, textTransform: 'none' }}
                                    >
                                        Filters
                                    </Button>
                                )}
                                {/* Sort */}
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel>Sort by</InputLabel>
                                    <Select
                                        value={sortBy}
                                        label="Sort by"
                                        onChange={(e) => setSortBy(e.target.value as SortOption)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {SORT_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Loading State */}
                        {loading ? (
                            <Box>
                                {[1, 2, 3].map((i) => (
                                    <Card key={i} sx={{ display: 'flex', mb: 2, borderRadius: 2 }}>
                                        <Skeleton variant="rectangular" width={240} height={220} />
                                        <CardContent sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="60%" height={32} />
                                            <Skeleton variant="text" width="40%" />
                                            <Skeleton variant="text" width="80%" />
                                            <Skeleton variant="rectangular" height={40} sx={{ mt: 2, borderRadius: 1 }} />
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        ) : filteredHotels.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                                <Hotel sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    No hotels found
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Try adjusting your filters or search criteria
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setFilters({
                                            priceRange: [0, 500],
                                            starRatings: [],
                                            reviewScore: 0,
                                            propertyTypes: [],
                                            amenities: [],
                                            neighborhoods: [],
                                            freeCancellation: false,
                                            payLater: false,
                                            distanceFromCenter: 50,
                                        })
                                    }
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                >
                                    Clear All Filters
                                </Button>
                            </Paper>
                        ) : (
                            <Box>
                                {filteredHotels.map((hotel) => (
                                    <HotelCard
                                        key={hotel.id}
                                        hotel={hotel}
                                        nights={nights}
                                        onSelect={handleSelectHotel}
                                    />
                                ))}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            {/* Mobile Filter Drawer */}
            <Drawer
                anchor="bottom"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        maxHeight: '85vh',
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Filters
                        </Typography>
                        <IconButton onClick={() => setFilterDrawerOpen(false)}>
                            <Close />
                        </IconButton>
                    </Box>
                    <FilterSidebar
                        filters={filters}
                        onChange={setFilters}
                        neighborhoods={neighborhoods}
                        maxPrice={Math.max(...hotels.map((h) => h.pricePerNight), 500)}
                        resultCount={filteredHotels.length}
                    />
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() =>
                                setFilters({
                                    priceRange: [0, 500],
                                    starRatings: [],
                                    reviewScore: 0,
                                    propertyTypes: [],
                                    amenities: [],
                                    neighborhoods: [],
                                    freeCancellation: false,
                                    payLater: false,
                                    distanceFromCenter: 50,
                                })
                            }
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                            Clear All
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setFilterDrawerOpen(false)}
                            sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}
                        >
                            Show {filteredHotels.length} Results
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
};

export default HotelSearchResultsPage;
